import { opacify } from "polished";
import React from "react";
import styled from "styled-components";

import * as Color from "~/Color";

import {
  Props as IconProps,
  ExclamationCircle,
  ExclamationTriangle,
} from "~/Icon";

import * as Layout from "~/Layout";
import * as Typography from "~/Typography";

export type ErrorMessage = {
  readonly danger?: boolean;
  readonly warning?: boolean;
  readonly notice?: boolean;
};

export const defaultMessage = ({ danger }: ErrorMessage): string =>
  danger ? "Error" : "Warning";

export const Component: Component<
  ErrorMessage & {
    readonly icon?: boolean;
    readonly children?: Component.Children;
  }
> = ({ icon, children, notice = false, ...props }) => (
  <Container.Component {...props}>
    <Typography.Component strong={!notice}>
      {icon && (
        <Layout.Inline.Component right>
          <Icon.Component faded={notice} {...props} />
        </Layout.Inline.Component>
      )}
      {children ?? defaultMessage(props)}
    </Typography.Component>
  </Container.Component>
);

namespace Container {
  export const Component = styled.span<ErrorMessage>`
    ${({ danger }) => (danger ? `color: ${Danger.color()};` : "")}
  `;
}

export namespace Icon {
  export const Component: React.Fn<IconProps & ErrorMessage> = ({
    danger,
    ...props
  }) =>
    danger ? (
      <Danger.Icon.Component {...props} />
    ) : (
      <Warning.Icon.Component {...props} />
    );
}

export namespace Danger {
  export const color = (): Color.Color => Color.guardsmanRed();

  export namespace Background {
    export const color = (): Color.Color => opacify(-0.97)(Danger.color());
    export const hover = (): Color.Color => opacify(0.03)(color());
  }

  export namespace Icon {
    export const Component = ExclamationTriangle;
  }
}

export namespace Warning {
  export const color = (): Color.Color => Color.mustard();
  export namespace Icon {
    export const Component = ExclamationCircle;
  }
}

export namespace Remedy {
  export const contactSupport = () =>
    "chat with support for more help" as const;

  export namespace Retry {
    export const now = () => "try again" as const;
    export const later = () => "try again later" as const;
  }
}
